import type { LoadingStates } from 'Store'
import { createAxiosAsyncThunk, getAxiosInstance, type ValidationErrors } from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { IS_HSL_SERVICE_ENVIRONMENT } from 'Util/constants'
import { type AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

const initialState: UserState = {
  loading: 'idle',
  authed: false, // Determined by fetchCurrentUser against the backend, not by inspecting cookies client-side
  user: null
}

export interface UserState {
  loading: LoadingStates
  authed: boolean
  user: UserShape | null
}

export enum GroupType {
  authoritySuperuser = 'authority_superuser',
  authorityAdmin = 'authority_admin',
  authorityUser = 'authority_user',
  spmUser = 'spm_user'
}

export interface UserShape {
  id: number
  email: string
  groups: GroupType[]
  pta: string
}

export const isSuperuser = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.authoritySuperuser)
}

export const isAuthorityAdmin = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.authorityAdmin)
}

export const isAuthorityUser = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.authorityUser)
}

export const isSPMUser = (user: UserShape | null) => {
  if (!user) return false
  return user.groups.includes(GroupType.spmUser)
}

export const isSuperuserOrSPMUser = (user: UserShape | null) => {
  return isSuperuser(user) || isSPMUser(user)
}

export const canAccessProductManagement = (user: UserShape | null) => {
  if (!user) return false
  // Superusers are allowed to access all services
  if (isSuperuser(user)) return true
  // Authority admins are allowed to access product management
  return isAuthorityAdmin(user)
}

export const canAccessSalesManagement = (user: UserShape | null) => {
  if (!user) return false
  // HSL users are not allowed to access sales management
  if (IS_HSL_SERVICE_ENVIRONMENT) return false
  return isSuperuser(user) || isAuthorityAdmin(user) || isAuthorityUser(user)
}

export const canAccessServiceProviderManagement = (user: UserShape | null) => {
  if (!user) return false
  return isSuperuserOrSPMUser(user)
}

export const canAccessControlDataManagement = (user: UserShape | null) => {
  if (!user) return false
  return isSuperuser(user)
}

export const canAccessSchoolManagement = (user: UserShape | null) => {
  if (!user) return false
  if (IS_HSL_SERVICE_ENVIRONMENT) return false
  return isSuperuser(user) || isAuthorityAdmin(user)
}

/**
 * Returns true if the user has access to at least one section of the app.
 *
 * **Must be updated whenever a new top-level service section is added.**
 */
export const canAccessApp = (user: UserShape | null) => {
  return (
    canAccessSalesManagement(user) ||
    canAccessProductManagement(user) ||
    canAccessServiceProviderManagement(user) ||
    canAccessControlDataManagement(user) ||
    canAccessSchoolManagement(user)
  )
}

type FetchCurrentUserResponse = UserShape

const fetchCurrentUser = createAxiosAsyncThunk<FetchCurrentUserResponse, undefined>(
  'user/fetchCurrentUser',
  '/api/v1/users/current/'
)

type LogOutResponse = Record<string, unknown>

const logOutUser = createAsyncThunk<
  LogOutResponse,
  undefined,
  {
    rejectValue: ValidationErrors
  }
>('user/logOut', async (_data, { rejectWithValue }) => {
  try {
    const response = await getAxiosInstance().post<LogOutResponse>(
      `/api/oidc/logout/`,
      JSON.stringify([])
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

const genericActions = actionGenerator('user')

const userSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCurrentUser.fulfilled, (state, { payload }) => {
        state.authed = true
        state.user = payload
      })
      .addCase(fetchCurrentUser.rejected, (state, { payload }) => {
        // Only treat this as "logged out" when the backend explicitly says so (401/403).
        // Transient failures (network errors, timeouts, 5xx) should not clear a valid session.
        const status = (payload as ValidationErrors | undefined)?.status
        if (status === 401 || status === 403) {
          state.authed = false
          state.user = null
        }
      })
      .addCase(logOutUser.fulfilled, state => {
        state.authed = false
        state.user = null
      })
      .addCase(logOutUser.rejected, state => {
        state.authed = false
        state.user = null
      })
      .addCase(genericActions.ResetAction, () => initialState)
      .addMatcher(genericActions.isPendingAction, state => {
        state.loading = 'pending'
      })
      .addMatcher(genericActions.isRejectedAction, state => {
        state.loading = 'rejected'
      })
      .addMatcher(genericActions.isFulfilledAction, state => {
        state.loading = 'fulfilled'
      })
      .addMatcher(
        (action: AnyAction) => action.type.endsWith('/rejected'),
        (state, { payload }) => {
          // Any request in the app getting a 401 means the session itself is no longer
          // valid, so de-authenticate everywhere.
          if ((payload as ValidationErrors | undefined)?.status === 401) {
            state.authed = false
            state.user = null
          }
        }
      )
  }
})

export default userSlice.reducer

export { fetchCurrentUser, logOutUser }
